import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const saintPaulDeVenceEn: ChauffeurPage = {
  slug: "saint-paul-de-vence",
  title: "VTC & private chauffeur in Saint-Paul-de-Vence",
  subtitle:
    "Private chauffeur in Saint-Paul-de-Vence: the hilltop art village, the Maeght Foundation, dinner at La Colombe d’Or and a ~30 min transfer to Nice airport.",
  keywords: [
    "vtc saint-paul-de-vence",
    "private chauffeur saint-paul-de-vence",
    "maeght foundation chauffeur",
    "saint-paul-de-vence nice airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Saint-Paul-de-Vence",
    subtitle:
      "The perched art village, its galleries and ramparts: a chauffeur who drops you as close as possible to the pedestrian lanes, with no parking to chase.",
    keywords: [
      "VTC Saint-Paul-de-Vence",
      "Maeght Foundation chauffeur",
      "Nice airport transfer",
      "art village chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Saint-Paul-de-Vence",
    badges: [
      "Pedestrian village",
      "Maeght Foundation",
      "~30 min from the airport",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Saint-Paul-de-Vence",
    items: [
      {
        title: "Drop-off at the ramparts",
        description:
          "The medieval core is fully pedestrian and parking is scarce nearby: the chauffeur leaves you at the village gate and returns on request, with no circling for a space.",
      },
      {
        title: "Art village & galleries",
        description:
          "From the Maeght Foundation to Rue Grande and its studios, up to the cemetery where Chagall rests: a full day of art with no logistics to juggle.",
      },
      {
        title: "Dinner at La Colombe d’Or",
        description:
          "There and back for the village’s legendary table and its painting collection: you enjoy dinner while the chauffeur handles the late drive to your hotel.",
      },
      {
        title: "~30 minutes from the airport",
        description:
          "A direct transfer to and from Nice Côte d’Azur airport via the A8 and Cagnes-sur-Mer, with flight tracking and a personal welcome.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Saint-Paul-de-Vence",
    services: [
      {
        title: "Hilltop village visits",
        text: "Drop-off at the ramparts gate, waiting while you wander Rue Grande, and pick-up at the agreed time with no parking constraint.",
      },
      {
        title: "Maeght Foundation & galleries",
        text: "Access to the Maeght Foundation and the art galleries, with waiting time included so you can visit at your own pace.",
      },
      {
        title: "Village dining",
        text: "La Colombe d’Or, Le Saint-Paul and the tables nearby: an evening there and back in complete peace of mind, no driving after dinner.",
      },
      {
        title: "Nice airport transfers",
        text: "Saint-Paul-de-Vence ⇄ Nice airport in about 30 minutes, with flight tracking and a welcome on arrival.",
      },
      {
        title: "Hourly hire & the backcountry",
        text: "By the hour or by the day to combine Saint-Paul, Vence, La Colle-sur-Loup and the Gorges du Loup with a dedicated chauffeur.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "The ramparts gate",
        description:
          "Porte de Vence and its car parks, at the foot of the pedestrian village.",
      },
      {
        name: "Rue Grande",
        description:
          "The central lane lined with galleries and artists’ studios.",
      },
      {
        name: "Maeght Foundation",
        description:
          "Modern-art museum and its sculpture gardens, outside the walls.",
      },
      {
        name: "La Colombe d’Or",
        description:
          "Historic hotel-restaurant and its collection of artworks.",
      },
      {
        name: "Village cemetery",
        description: "Marc Chagall’s grave, overlooking the ramparts.",
      },
      {
        name: "La Colle-sur-Loup",
        description:
          "Neighbouring village on the road to the backcountry and the Gorges du Loup.",
      },
    ],
  },
  generic: {
    heading: "A chauffeur for the art village",
    subheading:
      "Saint-Paul-de-Vence is earned on foot; the approach and the return are handled at the wheel.",
    paragraphs: [
      "Perched on its spur between Nice and Vence, Saint-Paul-de-Vence is explored on foot, inside ramparts closed to traffic. Spaces are scarce nearby in season: the chauffeur drops you as close as possible to the village gate and collects you on request, so parking never enters the picture.",
      "KS Rent Services works with fixed fares agreed in advance — for example Saint-Paul-de-Vence ⇄ Nice airport — and with hourly or daily hire, with an immediate quote. Our bilingual French/English chauffeurs, based on the French Riviera, are rated 5.0/5 from 38 Google reviews.",
      "A single day can string together the Maeght Foundation, lunch on Rue Grande and a run out to La Colle-sur-Loup or the Gorges du Loup: a dedicated chauffeur keeps the thread of the programme and adjusts timings to your visit.",
    ],
    bullets: [
      "Drop-off at the ramparts, pick-up on request",
      "Fixed fares & hourly hire, immediate quote",
      "Bilingual FR/EN chauffeurs, 5.0/5 from 38 Google reviews",
    ],
  },
  process: {
    heading: "Book a chauffeur in Saint-Paul-de-Vence",
    steps: [
      "Share your programme: village, Maeght Foundation, restaurant or airport transfer.",
      "We confirm the right vehicle and the fixed fare or the hourly hire.",
      "The chauffeur drops you at the ramparts gate or at the address you want.",
      "Visit or dinner at your own pace, pick-up on request for the rest.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Saint-Paul-de-Vence"),
    {
      q: "How long does it take to reach Nice airport?",
      a: "Allow about 30 minutes between Saint-Paul-de-Vence and Nice Côte d’Azur airport, via Cagnes-sur-Mer and the A8. The chauffeur tracks your flight and adjusts the departure time accordingly.",
    },
    {
      q: "Can you drive into the village?",
      a: "The heart of Saint-Paul-de-Vence is entirely pedestrian. The chauffeur drops you as close as possible to the ramparts gate, then collects you at the same spot after your visit.",
    },
    {
      q: "Can you take us to the Maeght Foundation and wait?",
      a: "Yes. We drop you at the Maeght Foundation, just outside the village walls, with waiting time included so you can take in the collections and sculpture gardens at your own pace.",
    },
    {
      q: "Do you handle the return after dinner at La Colombe d’Or?",
      a: "Yes, both the journey there and the late return are covered. You enjoy dinner with no driving to worry about, and the chauffeur takes you back to your hotel at the agreed time.",
    },
    {
      q: "How far in advance should I book?",
      a: "In summer and during exhibitions, book as early as possible to secure the vehicle; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to the international clientele of Saint-Paul-de-Vence’s galleries and hotels.",
    },
    {
      q: "Which vehicles do you offer for Saint-Paul-de-Vence?",
      a: "A Mercedes sedan, a van or an SUV depending on the number of passengers, suited to the narrow roads leading up to the village.",
    },
  ],
  relatedLinks: [
    { label: "Nice Airport transfers", href: "/en/chauffeur/airport" },
    { label: "Book a chauffeur in Nice", href: "/en/chauffeur/nice" },
    {
      label: "Private chauffeur on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Private car service in Cannes", href: "/en/chauffeur/cannes" },
  ],
  cta: {
    title: "Your private chauffeur in Saint-Paul-de-Vence",
    text: "Village visit, Maeght Foundation or airport transfer: book your chauffeur.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Saint-Paul-de-Vence — car service 24/7",
    description:
      "Private car service in Saint-Paul-de-Vence: art village, Maeght Foundation, La Colombe d’Or and a ~30 min transfer to Nice airport. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/saint-paul-de-vence",
  },
};

export default saintPaulDeVenceEn;
